import {
  normalizeRouteStop,
  stopHasNavigableCoords,
  type RouteStop,
  type RouteStopPriority,
} from '../models/RouteStop';

export type OptimizeableStop = Pick<
  RouteStop,
  'id' | 'lat' | 'lng' | 'priority' | 'status' | 'sequence' | 'kind'
>;

function haversineM(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function coordsOf(stop: OptimizeableStop): { lat: number; lng: number } | null {
  if (!stopHasNavigableCoords(stop)) return null;
  return { lat: Number(stop.lat), lng: Number(stop.lng) };
}

/** Nearest-neighbor then 2-opt within a bucket. Stops without coords keep input order at end. */
export function orderStopsInBucket(
  stops: OptimizeableStop[],
  start?: { lat: number; lng: number } | null,
): OptimizeableStop[] {
  if (stops.length <= 1) return [...stops];

  const withCoords: OptimizeableStop[] = [];
  const without: OptimizeableStop[] = [];
  for (const s of stops) {
    if (coordsOf(s)) withCoords.push(s);
    else without.push(s);
  }
  if (withCoords.length <= 1) return [...withCoords, ...without];

  const remaining = [...withCoords];
  const ordered: OptimizeableStop[] = [];
  let cursor = start ?? coordsOf(remaining[0])!;

  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const c = coordsOf(remaining[i])!;
      const d = haversineM(cursor, c);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const [next] = remaining.splice(bestIdx, 1);
    ordered.push(next);
    cursor = coordsOf(next)!;
  }

  return [...twoOpt(ordered), ...without];
}

function pathLength(stops: OptimizeableStop[]): number {
  let total = 0;
  for (let i = 1; i < stops.length; i += 1) {
    const a = coordsOf(stops[i - 1]);
    const b = coordsOf(stops[i]);
    if (a && b) total += haversineM(a, b);
  }
  return total;
}

function twoOpt(stops: OptimizeableStop[]): OptimizeableStop[] {
  if (stops.length < 4) return stops;
  let best = [...stops];
  let improved = true;
  let guard = 0;
  while (improved && guard < 40) {
    improved = false;
    guard += 1;
    const n = best.length;
    for (let i = 0; i < n - 2; i += 1) {
      for (let k = i + 1; k < n - 1; k += 1) {
        const candidate = [
          ...best.slice(0, i + 1),
          ...best.slice(i + 1, k + 1).reverse(),
          ...best.slice(k + 1),
        ];
        if (pathLength(candidate) + 1e-6 < pathLength(best)) {
          best = candidate;
          improved = true;
        }
      }
    }
  }
  return best;
}

export function stopPriority(stop: OptimizeableStop): RouteStopPriority {
  return stop.priority === 'high' ? 'high' : 'normal';
}

/**
 * High-priority pending stops first (NN/2-opt), then remaining pending, then
 * house HQ (destination), then cleared/skipped.
 * `opts.start` is typically the runner's live GPS when they open the app.
 * Assigns 1-based `sequence` on all returned stops.
 */
export function optimizeRouteOrder(
  stops: OptimizeableStop[],
  opts?: { start?: { lat: number; lng: number } | null },
): RouteStop[] {
  const normalized = stops.map((s) => normalizeRouteStop(s as RouteStop));
  const active = normalized.filter(
    (s) => s.status !== 'cleared' && s.status !== 'skipped',
  );
  const done = normalized.filter(
    (s) => s.status === 'cleared' || s.status === 'skipped',
  );

  const destinations = active.filter((s) => s.kind === 'house');
  const movable = active.filter((s) => s.kind !== 'house');
  const high = movable.filter((s) => stopPriority(s) === 'high');
  const normal = movable.filter((s) => stopPriority(s) !== 'high');

  const orderedHigh = orderStopsInBucket(high, opts?.start ?? null);
  const lastHigh = orderedHigh.length > 0
    ? coordsOf(orderedHigh[orderedHigh.length - 1])
    : (opts?.start ?? null);
  const orderedNormal = orderStopsInBucket(normal, lastHigh);

  const ordered = [...orderedHigh, ...orderedNormal, ...destinations, ...done];
  return ordered.map((stop, idx) => normalizeRouteStop({
    ...stop,
    sequence: idx + 1,
  }));
}

/** Sort by sequence ascending; missing sequence sorts after numbered stops. */
export function sortStopsBySequence<T extends { id: string; sequence?: number | null }>(
  stops: T[],
): T[] {
  return [...stops].sort((a, b) => {
    const sa = a.sequence == null ? Number.POSITIVE_INFINITY : Number(a.sequence);
    const sb = b.sequence == null ? Number.POSITIVE_INFINITY : Number(b.sequence);
    if (sa !== sb) return sa - sb;
    return String(a.id).localeCompare(String(b.id));
  });
}
